import * as angular from 'angular';
import * as _ from 'lodash';

import { Pattern, ComponentDefinition, Dictionary } from '../../models';
import { tokenize, getRegExpCase, Helper } from '../../common/helpers';

interface SortConfig<T> {
    [token: string]: {
        callback: (value: RegExp) => (value: T) => string | number;
        direction: 'asc' | 'desc';
    };
}

class GenericSearchBarController<T> implements angular.IComponentController {
    filteredItems: T[];
    formController: angular.IFormController;
    onChange: (value) => void;
    onFilter: (value: { items: T[] }) => void;
    search: { [index: string]: string };
    searchId: string = Helper.getGuid();
    searchString: string;

    readonly criteria: Pattern<T>[];
    readonly isDisabled: boolean;
    readonly items: T[];
    readonly propertyName: string;
    readonly searchTitle: string;
    readonly sort: SortConfig<T> | undefined;
    readonly stopPropagationOnClear: boolean;

    static $inject = ['$scope', '$element'];

    constructor(
        private _scope: angular.IScope,
        private _$element: angular.IAugmentedJQuery
    ) {
    }

    $onInit() {
        this.search = {};
        if (!this.criteria) {
            console.error('Missing configuration. Criteria property is required.');
        } else {
            if (!this.filteredItems) {
                this.filteredItems = [];
            }
            this._scope.$watchGroup([() => this.searchString, () => this.items], () => {
                this.filter();
            });
        }
        const self = this;
        this._$element
            .find('a.dropdown-toggle')
            .on('click', function (e) {
                if (!self.isDisabled) {
                    $(this).next('ul').toggle();
                    e.stopPropagation();
                    e.preventDefault();
                }
            });
    }

    cancel($event: angular.IAngularEvent) {
        this._$element.find('a.dropdown-toggle')
            .next('ul')
            .toggle();

        $event.stopPropagation();
        $event.preventDefault();
    }

    change() {
        this.search = tokenize(this.searchString);
        if (this.onChange) {
            this.onChange({ value: this.searchString });
        }
    }

    clear($event: angular.IAngularEvent): void {
        this.searchString = '';
        this.change();
        if(this.stopPropagationOnClear){
            $event.stopPropagation();
        }
    }

    stopPropagation($event: angular.IAngularEvent) {
        const classes = ($event as any).target.className + ($event as any).target.parentElement.className;
        if (classes.indexOf('dropdown-close') <= 0) {
            $event.stopPropagation();
        }
    }

    computeSearch($event: angular.IAngularEvent) {
        this.searchString = this.convertToSearchString();
        if (this.onChange) {
            this.onChange({ value: this.searchString });
        }
        this._$element.find('ul.dropdown-menu').toggle();
        $event.stopPropagation();
    }

    convertToSearchString(): string {
        let search = this.search.name ? this.search.name + ' ' : '';
        this.forEachKeyword((key) => {
            if (this.search[key] && this.search[key].length > 0 && key !== 'name') {
                search += key ? `${key}:${this.search[key]} ` : `${this.search[key]} `;
            }
        });
        return search.trim();
    }

    watchKeyPress($event) {
        if ($event.keyCode === 13) {
            this.computeSearch($event);
        }
    }

    private filter() {
        if (this.searchString) {
            const tokenized = tokenize(this.searchString);
            this.search = tokenized;
            if (this.items) {
                let filteredItems = this.items.map(d => d);
                const keys = Object.keys(tokenized);
                filteredItems = this.filterWithoutTokens(keys, tokenized, filteredItems);
                filteredItems = this.filterByToken(tokenized, filteredItems);
                if (this.sort) {
                    const sortKeys = Object.keys(this.sort);
                    sortKeys.filter(sk => keys.indexOf(sk) >= 0)
                        .forEach(sk => {
                            filteredItems = _.orderBy(filteredItems, this.sort[sk].callback(getRegExpCase(tokenized[sk], true)), [this.sort[sk].direction]);
                        });

                }
                this.filteredItems = filteredItems;
            } else {
                if (this.items) {
                    this.filteredItems = this.items.map(d => d);
                }
            }
        } else {
            this.filteredItems = this.items ? this.items.map(d => d) : [];
        }
        if (this.onFilter) {
            this.onFilter({ items: this.filteredItems });
        }
    }

    private filterByToken(tokenized: Dictionary<string>, filteredItems: T[]) {
        for (const key of Object.keys(tokenized)) {
            if (_.some(this.criteria, c => c.property === key)) {
                const c: Pattern<T> = _.find(this.criteria, c => c.property === key);
                const value = tokenized[key];
                const reg = getRegExpCase(value, true);
                filteredItems = filteredItems.filter(i => c.isMatch(i, reg, value));
            }
        }
        return filteredItems;
    }

    private filterWithoutTokens(keys: string[], tokenized: Dictionary<string>, filteredItems: T[]) {
        if (keys.length && !keys[0]) {
            const value = tokenized[keys[0]];
            const reg = getRegExpCase(value, true);
            filteredItems = filteredItems.filter(i => _.some(this.criteria, c => c.isMatch(i, reg, value)));
        }
        return filteredItems;
    }

    private forEachKeyword(method: (key: string) => void) {
        for (const key of Object.keys(this.search)) {
            if (this.search.hasOwnProperty(key)) {
                method(key);
            }
        }
    }
}

export const wtGenericSearchBar: ComponentDefinition = {
    componentId: 'wtGenericSearchBar',
    controller: GenericSearchBarController,
    controllerAs: 'vm',
    template: require('./genericSearchBar.component.html'),
    bindings: {
        criteria: '<',
        filteredItems: '=',
        formController: '=?',
        isDisabled: '<?',
        items: '<',
        onChange: '&?',
        onFilter: '&?',
        placeholder: '@',
        propertyName: '<?',
        searchString: '=?',
        searchTitle: '<?',
        sort: '<?',
        stopPropagationOnClear: '<?'
    }
};